import { writable, derived } from 'svelte/store';

// Comments panel state
function createCommentsStore() {
    const { subscribe, set, update } = writable({
        isOpen: false,
        position: { left: window.innerWidth - 400, top: 100 },
        width: 380,
        isDragging: false,
        isResizing: false,
        filter: 'all', // all, unresolved, resolved, mine
        sortBy: 'newest', // newest, oldest, position
        selectedCommentId: null
    });

    return {
        subscribe,
        open: () => update(state => ({ ...state, isOpen: true })),
        close: () => update(state => ({ ...state, isOpen: false })),
        toggle: () => update(state => ({ ...state, isOpen: !state.isOpen })),
        setPosition: (left, top) => update(state => ({ ...state, position: { left, top } })),
        setWidth: (width) => update(state => ({ ...state, width })),
        setDragging: (isDragging) => update(state => ({ ...state, isDragging })),
        setResizing: (isResizing) => update(state => ({ ...state, isResizing })),
        setFilter: (filter) => update(state => ({ ...state, filter })),
        setSortBy: (sortBy) => update(state => ({ ...state, sortBy })),
        selectComment: (commentId) => update(state => ({ ...state, selectedCommentId: commentId })),
        reset: () => set({
            isOpen: false,
            position: { left: window.innerWidth - 400, top: 100 },
            width: 380,
            isDragging: false,
            isResizing: false,
            filter: 'all',
            sortBy: 'newest',
            selectedCommentId: null
        })
    };
}

// Comment threads organized by document and position
function createCommentThreadsStore() {
    const { subscribe, set, update } = writable({
        threads: new Map(), // Map<threadId, Thread>
        activeThreadId: null
    });

    return {
        subscribe,
        addThread: (thread) => update(state => {
            const newThreads = new Map(state.threads);
            newThreads.set(thread.id, thread);
            return { ...state, threads: newThreads };
        }),
        updateThread: (threadId, updates) => update(state => {
            const newThreads = new Map(state.threads);
            const thread = newThreads.get(threadId);
            if (thread) {
                newThreads.set(threadId, { ...thread, ...updates });
            }
            return { ...state, threads: newThreads };
        }),
        removeThread: (threadId) => update(state => {
            const newThreads = new Map(state.threads);
            newThreads.delete(threadId);
            return { ...state, threads: newThreads };
        }),
        setActiveThread: (threadId) => update(state => ({ ...state, activeThreadId: threadId })),
        reset: () => set({ threads: new Map(), activeThreadId: null })
    };
}

// Create store instances
export const commentsStore = createCommentsStore();
export const commentThreadsStore = createCommentThreadsStore();

// Derived store for filtered and sorted threads
export const filteredThreads = derived(
    [commentThreadsStore, commentsStore],
    ([$threads, $comments]) => {
        let threads = Array.from($threads.threads.values());
        
        // Apply filter
        switch ($comments.filter) {
            case 'unresolved':
                threads = threads.filter(t => !t.resolved);
                break;
            case 'resolved':
                threads = threads.filter(t => t.resolved);
                break;
            case 'mine':
                threads = threads.filter(t => 
                    t.userId === window.currentUser?.id || 
                    t.replies?.some(r => r.userId === window.currentUser?.id)
                );
                break;
        }
        
        // Apply sort
        switch ($comments.sortBy) {
            case 'newest':
                threads.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                break;
            case 'oldest':
                threads.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                break;
            case 'position':
                threads.sort((a, b) => a.position - b.position);
                break;
        }
        
        return threads;
    }
);